import { Grid } from "@mui/material";
import { EditDropDownListComponentBox, EmptyBox } from "../../styles/theme";
import { useEffect, useState, useCallback } from "react";
import { VehicleData } from "../../system/types/Reservation";
import { FilteringEventArgs } from "@syncfusion/ej2-react-dropdowns";
import { Query } from "@syncfusion/ej2-data";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import dayjs from "dayjs";
import { VehicleApi } from "../../system/api/VehicleApi";
import { VehicleLastVehicleInfo } from "../../system/types/Vehicle";
import { ErrorHandler } from "../../system/ApiService";
import AlertModal from "../Common/AlertModal";
import { Mobile, Pc } from "../../pages/Layout/ResponsiveScreen";
import DrivingHistoryTable from "../Admin/Vehicle/DrivingHistoryTable";
import DrivingHistoryCard from "../Admin/Vehicle/DrivingHistoryCard";
import { VehicleHistoryType } from "../../system/Constants";

const INITIAL_DATE = dayjs().format("YYYY-MM-DD");
const DATE_FORMAT = "YYYY-MM-DD";
const VEHICLE_FIELDS = {
  dataSource: [],
  value: "id",
  text: "number",
  groupBy: "sizeType",
};

function VehicleHistoryTable() {
  const [vehicleAllData, setVehicleAllData] = useState<VehicleData[]>([]);
  const [selectedDate, setSelectedDate] = useState(INITIAL_DATE);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null
  );
  const [drivingData, setDrivingData] = useState<VehicleLastVehicleInfo[]>([]);

  // API 에러 핸들링 공통 함수
  const handleApiError = useCallback((err: any) => {
    const msg = ErrorHandler(err);
    AlertModal("msg", msg);
  }, []);

  // 운행 이력 데이터 조회
  const getDrivingData = useCallback(() => {
    VehicleApi.getDrivingHistory(
      selectedDate,
      selectedDate,
      false,
      selectedVehicleId,
      null
    )
      .then((res) => {
        setDrivingData(res.data);
      })
      .catch(handleApiError);
  }, [selectedDate, selectedVehicleId, handleApiError]);

  // 차량 목록 조회
  const getVehicleList = useCallback(() => {
    VehicleApi.getVehicle(0, 9999999)
      .then((res) => {
        const filteredVehicles = res.data.vehicles.filter(
          (vehicle: VehicleData) => vehicle.designatedExecutives === null
        );
        setVehicleAllData(filteredVehicles);
      })
      .catch(handleApiError);
  }, [handleApiError]);

  // 차량 선택 핸들러
  const handleVehicleChange = useCallback((e: any) => {
    setSelectedVehicleId(e.target.value);
  }, []);

  // 날짜 변경 핸들러
  const handleDateChange = useCallback((args: any) => {
    setSelectedDate(dayjs(args.value).format(DATE_FORMAT));
  }, []);

  // 차량 필터링 핸들러
  const handleVehicleFiltering = useCallback(
    (args: FilteringEventArgs) => {
      let query = new Query();
      if (args.text !== "") {
        const filteredData = vehicleAllData.filter(
          (vehicle: VehicleData) =>
            vehicle.name?.toLowerCase().includes(args.text.toLowerCase()) ||
            vehicle.number?.toLowerCase().includes(args.text.toLowerCase())
        );
        args.updateData(filteredData as any);
      } else {
        args.updateData(vehicleAllData as any, query);
      }
    },
    [vehicleAllData]
  );

  // 차량 목록 초기 로드
  useEffect(() => {
    getVehicleList();
  }, [getVehicleList]);

  // 선택 조건 변경 시 데이터 새로고침
  useEffect(() => {
    if (selectedVehicleId) {
      getDrivingData();
    }
  }, [getDrivingData, selectedVehicleId]);

  // 차량 데이터소스 객체 (메모이제이션)
  const vehicleDataSource = {
    ...VEHICLE_FIELDS,
    dataSource: vehicleAllData,
  };

  const itemTemplate = (data: VehicleData) => {
    return (
      <Grid container justifyContent="space-between">
        <Grid>{data.name}</Grid>
        <Grid>{data.number}</Grid>
      </Grid>
    );
  };

  return (
    <Grid container item xs={12} mt="20px">
      <Grid
        container
        item
        xs={12}
        justifyContent="space-between"
        alignItems="baseline"
        mt="10px"
      >
        {/* 날짜 선택 */}
        <Grid item sm={6} md={3}>
          <DatePickerComponent
            name="selectedDate"
            value={new Date(selectedDate)}
            placeholder="조회할 날짜를 선택해주세요."
            format="yyyy-MM-dd"
            locale="ko"
            onChange={handleDateChange}
            showClearButton={false}
          />
        </Grid>

        {/* 차량 선택 */}
        <Grid item sm={6} md={3} container justifyContent="right">
          <EditDropDownListComponentBox
            locale="ko"
            showClearButton={true}
            dataSource={vehicleAllData}
            value={selectedVehicleId}
            onChange={handleVehicleChange}
            placeholder="차량을 선택해주세요."
            autoComplete="new-password"
            filtering={handleVehicleFiltering}
            allowFiltering={true}
            fields={vehicleDataSource}
            itemTemplate={itemTemplate}
          />
        </Grid>

        {/* 운행 이력 테이블/카드 */}
        <Grid container item xs={12} mt="10px">
          <Pc>
            <DrivingHistoryTable
              drivingData={drivingData}
              refreshData={getDrivingData}
              type={VehicleHistoryType.detail}
            />
          </Pc>
          <Mobile>
            <DrivingHistoryCard
              drivingData={drivingData}
              refreshData={getDrivingData}
            />
          </Mobile>

          {/* 빈 데이터 표시 */}
          {drivingData.length === 0 && (
            <EmptyBox container justifyContent="center">
              조회된 데이터가 없습니다.
            </EmptyBox>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default VehicleHistoryTable;
